import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { useAuth } from "../../../AuthContext";
import {
  FaUsers,
  FaEdit,
  FaTrash,
  FaPlus,
  FaFileExcel,
  FaChevronDown,
  FaChevronUp,
  FaUserPlus,
} from "react-icons/fa";

const API_BASE_URL = "https://sapl.cantarellisolucoes.com.br/wp-json/custom/v1";

const Comissoes = () => {
  const { user } = useAuth();
  const toast = useRef(null);

  // Estados
  const [loading, setLoading] = useState(false);
  const [comissoes, setComissoes] = useState([]);
  const [legislaturas, setLegislaturas] = useState([]);
  const [legislaturaSelecionada, setLegislaturaSelecionada] = useState(null);
  const [parlamentares, setParlamentares] = useState([]);

  // Estados de filtro
  const [termoPesquisa, setTermoPesquisa] = useState("");
  const [apenasAtivas, setApenasAtivas] = useState(false);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  // Estados do modal de comissão
  const [modalComissaoVisible, setModalComissaoVisible] = useState(false);
  const [editandoComissao, setEditandoComissao] = useState(null);
  const [formDataComissao, setFormDataComissao] = useState({
    nome: "",
    sigla: "",
    tipo: "permanente",
    ativa: true,
    data_criacao: null,
    data_extincao: null,
    observacoes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Estados do modal de membros
  const [modalMembroVisible, setModalMembroVisible] = useState(false);
  const [comissaoSelecionada, setComissaoSelecionada] = useState(null);
  const [membrosComissao, setMembrosComissao] = useState([]);
  const [formDataMembro, setFormDataMembro] = useState({
    parlamentar_id: null,
    cargo: "Membro",
  });
  const [submittingMembro, setSubmittingMembro] = useState(false);

  // Opções de tipos de comissão
  const tiposComissao = [
    { label: "Comissão Permanente", value: "permanente" },
    { label: "Comissão Temática", value: "tematica" },
    { label: "Comissão Temporária", value: "temporaria" },
    { label: "Comissão Parlamentar de Inquérito (CPI)", value: "cpi" },
    { label: "Comissão Especial", value: "especial" },
  ];

  // Opções de cargos na comissão
  const cargosDisponiveis = [
    { label: "Presidente", value: "Presidente" },
    { label: "Vice-Presidente", value: "Vice-Presidente" },
    { label: "Relator", value: "Relator" },
    { label: "Membro", value: "Membro" },
  ];

  // Função genérica para requisições fetch com token
  const apiRequest = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${user?.token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
        ...options.headers,
      },
      credentials: "include",
    });

    const data = await response.json();

    if (!response.ok) {
      throw { status: response.status, data };
    }

    return data;
  };

  // Carregar legislaturas da API
  const carregarLegislaturas = useCallback(async () => {
    try {
      const response = await apiRequest("/legislaturas");
      const legislaturasList = response.data.map((leg) => ({
        id: leg.id,
        name: leg.descricao,
        code: leg.numero,
        atual: leg.ativa === 1,
        data_inicio: leg.data_inicio,
        data_fim: leg.data_fim,
      }));
      setLegislaturas(legislaturasList);

      // Seleciona a legislatura atual por padrão
      const atual = legislaturasList.find((l) => l.atual);
      if (atual) setLegislaturaSelecionada(atual);
      else if (legislaturasList[0])
        setLegislaturaSelecionada(legislaturasList[0]);
    } catch (error) {
      console.error("Erro ao carregar legislaturas:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Não foi possível carregar as legislaturas",
        life: 5000,
      });
    }
  }, [user?.token]);

  // Carregar parlamentares
  const carregarParlamentares = useCallback(async () => {
    try {
      let url = "/parlamentares?ativo=true";
      if (legislaturaSelecionada?.id) {
        url += `&legislatura_id=${legislaturaSelecionada.id}`;
      }
      const response = await apiRequest(url);
      setParlamentares(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar parlamentares:", error);
    }
  }, [user?.token, legislaturaSelecionada]);

  // Carregar comissões
  const carregarComissoes = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/comissoes";
      const params = new URLSearchParams();

      if (apenasAtivas) params.append("ativa", "true");
      if (termoPesquisa) params.append("search", termoPesquisa);
      if (legislaturaSelecionada?.id)
        params.append("legislatura_id", legislaturaSelecionada.id);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await apiRequest(url);
      setComissoes(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar comissões:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Não foi possível carregar as comissões",
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [user?.token, legislaturaSelecionada, apenasAtivas, termoPesquisa]);

  // Carregar membros de uma comissão
  const carregarMembrosComissao = useCallback(async (comissaoId) => {
    try {
      const response = await apiRequest(`/comissoes/${comissaoId}/membros`);
      setMembrosComissao(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar membros:", error);
      setMembrosComissao([]);
    }
  }, []);

  // Efeitos de carregamento inicial
  useEffect(() => {
    if (user?.token) {
      carregarLegislaturas();
    }
  }, [user?.token, carregarLegislaturas]);

  useEffect(() => {
    if (user?.token && legislaturaSelecionada) {
      carregarParlamentares();
      carregarComissoes();
    }
  }, [
    user?.token,
    legislaturaSelecionada,
    carregarParlamentares,
    carregarComissoes,
  ]);

  // Abrir modal para criar comissão
  const abrirModalCriarComissao = () => {
    setEditandoComissao(null);
    setFormDataComissao({
      nome: "",
      sigla: "",
      tipo: "permanente",
      ativa: true,
      data_criacao: legislaturaSelecionada?.data_inicio
        ? new Date(legislaturaSelecionada.data_inicio)
        : new Date(),
      data_extincao: legislaturaSelecionada?.data_fim
        ? new Date(legislaturaSelecionada.data_fim)
        : null,
      observacoes: "",
    });
    setModalComissaoVisible(true);
  };

  // Abrir modal para editar comissão
  const abrirModalEditarComissao = (comissao) => {
    setEditandoComissao(comissao);
    setFormDataComissao({
      nome: comissao.nome || "",
      sigla: comissao.sigla || "",
      tipo: comissao.tipo || "permanente",
      ativa:
        comissao.ativa === 1 ||
        comissao.ativa === "1" ||
        comissao.ativa === true ||
        comissao.ativa === "true",
      data_criacao: comissao.data_criacao
        ? new Date(comissao.data_criacao)
        : null,
      // TRATAMENTO CORRETO PARA DATA DE EXTINÇÃO
      data_extincao:
        comissao.data_extincao &&
        comissao.data_extincao !== "" &&
        comissao.data_extincao !== "0000-00-00"
          ? new Date(comissao.data_extincao)
          : null,
      observacoes: comissao.observacoes || "",
    });
    setModalComissaoVisible(true);
  };

  // Salvar comissão
  const salvarComissao = async () => {
    if (!formDataComissao.nome.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "O nome da comissão é obrigatório",
        life: 3000,
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        nome: formDataComissao.nome,
        sigla: formDataComissao.sigla,
        tipo: formDataComissao.tipo,
        ativa: formDataComissao.ativa, // Já é boolean
        data_criacao: formDataComissao.data_criacao
          ? formDataComissao.data_criacao.toISOString().split("T")[0]
          : null,
        data_extincao: formDataComissao.data_extincao
          ? formDataComissao.data_extincao.toISOString().split("T")[0]
          : null,
        observacoes: formDataComissao.observacoes,
        legislatura_id: legislaturaSelecionada?.id,
      };

      console.log("Payload sendo enviado:", payload); // Para debug

      if (editandoComissao) {
        await apiRequest(`/comissoes/${editandoComissao.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Comissão atualizada com sucesso",
          life: 3000,
        });
      } else {
        await apiRequest("/comissoes", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Comissão criada com sucesso",
          life: 3000,
        });
      }

      setModalComissaoVisible(false);
      carregarComissoes();
    } catch (error) {
      console.error("Erro ao salvar comissão:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: error.data?.message || "Falha ao salvar comissão",
        life: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Excluir comissão
  const confirmarExclusaoComissao = (comissao) => {
    confirmDialog({
      header: "",
      style: { width: "400px" },
      className: "custom-confirm-dialog",

      message: (
        <div className="flex flex-column align-items-center text-center">
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: "3rem", color: "#f59e0b", marginBottom: "1rem" }}
          ></i>

          <span
            style={{
              fontSize: "1.1rem",
              fontWeight: "500",
              marginBottom: "0.5rem",
            }}
          >
            Confirmar exclusão
          </span>

          <span style={{ fontSize: "0.95rem", color: "#6b7280" }}>
            Deseja realmente <strong>excluir</strong> a comissão
          </span>

          <span
            style={{ fontSize: "1rem", fontWeight: "600", marginTop: "0.5rem" }}
          >
            {comissao.nome}?
          </span>
        </div>
      ),

      footer: (options) => (
        <div className="flex justify-content-center gap-2 w-full">
          <button
            className="p-button p-component p-button-sm p-button-secondary"
            onClick={options.reject}
          >
            <span className="p-button-label">Cancelar</span>
          </button>

          <button
            className="p-button p-component p-button-sm p-button-danger"
            onClick={options.accept}
          >
            <span className="p-button-label">Excluir</span>
          </button>
        </div>
      ),

      accept: async () => {
        try {
          await apiRequest(`/comissoes/${comissao.id}`, { method: "DELETE" });
          toast.current?.show({
            severity: "success",
            summary: "Excluído",
            detail: "Comissão removida com sucesso",
            life: 3000,
          });
          carregarComissoes();
        } catch (error) {
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: error.data?.message || "Falha ao excluir comissão",
            life: 5000,
          });
        }
      },
    });
  };

  // Abrir modal de membros
  const abrirModalMembros = async (comissao) => {
    setComissaoSelecionada(comissao);
    setFormDataMembro({
      parlamentar_id: null,
      cargo: "Membro",
    });
    await carregarMembrosComissao(comissao.id);
    setModalMembroVisible(true);
  };

  // Adicionar membro à comissão
  const adicionarMembro = async () => {
    if (!formDataMembro.parlamentar_id) {
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "Selecione um parlamentar",
        life: 3000,
      });
      return;
    }

    setSubmittingMembro(true);
    try {
      await apiRequest(`/comissoes/${comissaoSelecionada.id}/membros`, {
        method: "POST",
        body: JSON.stringify({
          parlamentar_id: formDataMembro.parlamentar_id,
          cargo: formDataMembro.cargo,
        }),
      });

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Membro adicionado à comissão",
        life: 3000,
      });

      setFormDataMembro({
        parlamentar_id: null,
        cargo: "Membro",
      });
      await carregarMembrosComissao(comissaoSelecionada.id);
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: error.data?.message || "Falha ao adicionar membro",
        life: 5000,
      });
    } finally {
      setSubmittingMembro(false);
    }
  };

  // Remover membro da comissão
  const confirmarRemocaoMembro = (membro) => {
    confirmDialog({
      header: "",
      style: { width: "400px" },
      className: "custom-confirm-dialog",

      message: (
        <div className="flex flex-column align-items-center text-center">
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: "3rem", color: "#f59e0b", marginBottom: "1rem" }}
          ></i>

          <span
            style={{
              fontSize: "1.1rem",
              fontWeight: "500",
              marginBottom: "0.5rem",
            }}
          >
            Confirmar remoção
          </span>

          <span style={{ fontSize: "0.95rem", color: "#6b7280" }}>
            Deseja realmente <strong>remover</strong> o parlamentar
          </span>

          <span
            style={{ fontSize: "1rem", fontWeight: "600", marginTop: "0.5rem" }}
          >
            {membro.nome}?
          </span>
        </div>
      ),

      footer: (options) => (
        <div className="flex justify-content-center gap-2 w-full">
          <button
            className="p-button p-component p-button-sm p-button-secondary"
            onClick={options.reject}
          >
            <span className="p-button-label">Cancelar</span>
          </button>

          <button
            className="p-button p-component p-button-sm p-button-danger"
            onClick={options.accept}
          >
            <span className="p-button-label">Remover</span>
          </button>
        </div>
      ),

      accept: async () => {
        try {
          await apiRequest(`/comissao-membros/${membro.id}`, {
            method: "DELETE",
          });
          toast.current?.show({
            severity: "success",
            summary: "Removido",
            detail: "Membro removido da comissão",
            life: 3000,
          });
          await carregarMembrosComissao(comissaoSelecionada.id);
        } catch (error) {
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: error.data?.message || "Falha ao remover membro",
            life: 5000,
          });
        }
      },
    });
  };

  // Templates da tabela de comissões
  const nomeBody = (rowData) => (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          backgroundColor: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#3b82f6",
          fontSize: "1.2rem",
        }}
      >
        <FaUsers />
      </div>
      <div>
        <div style={{ fontWeight: "500" }}>{rowData.nome}</div>
        {rowData.sigla && (
          <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
            Sigla: {rowData.sigla}
          </div>
        )}
      </div>
    </div>
  );

  const tipoBody = (rowData) => {
    const getTipoConfig = (tipo) => {
      const configs = {
        permanente: { label: "Permanente", severity: "info" },
        temporaria: { label: "Temporária", severity: "warning" },
        cpi: { label: "CPI", severity: "danger" },
        especial: { label: "Especial", severity: "secondary" },
      };
      return configs[tipo] || { label: tipo || "Permanente", severity: "info" };
    };

    const config = getTipoConfig(rowData.tipo);

    return <Tag value={config.label} severity={config.severity} />;
  };

  const dataCriacaoBody = (rowData) => {
    if (!rowData.data_criacao)
      return <span style={{ color: "#9ca3af" }}>---</span>;
    return new Date(rowData.data_criacao).toLocaleDateString("pt-BR");
  };

  const ativaBody = (rowData) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <Tag
        value={rowData.ativa == 1 ? "Ativo" : "Inativo"}
        severity={rowData.ativa == 1 ? "success" : "danger"}
        icon={rowData.ativa == 1 ? "pi pi-check-circle" : "pi pi-times-circle"}
      />
    </div>
  );

  const acoesBody = (rowData) => (
    <React.Fragment>
      <Button
        icon="pi pi-users"
        size="small"
        severity="info"
        tooltip="Gerenciar Membros"
        tooltipOptions={{ position: "left" }}
        className="mr-2 custom-button button-efeito"
        style={{ width: "32px", height: "32px" }}
        onClick={() => abrirModalMembros(rowData)}
      />
      <Button
        icon="pi pi-file-edit"
        size="small"
        severity="success"
        tooltip="Editar"
        tooltipOptions={{ position: "left" }}
        className="mr-2 custom-button button-efeito"
        style={{ width: "32px", height: "32px" }}
        onClick={() => abrirModalEditarComissao(rowData)}
      />
      <Button
        icon="pi pi-trash"
        size="small"
        severity="danger"
        tooltip="Excluir"
        tooltipOptions={{ position: "left" }}
        className="custom-button button-efeito"
        style={{ width: "32px", height: "32px" }}
        onClick={() => confirmarExclusaoComissao(rowData)}
      />
    </React.Fragment>
  );

  // Templates da tabela de membros
  const nomeMembroBody = (rowData) => (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <div
        style={{
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          backgroundColor: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#3b82f6",
        }}
      >
        <FaUsers size={14} />
      </div>
      <span style={{ fontWeight: "500" }}>{rowData.nome}</span>
    </div>
  );

  const cargoMembroBody = (rowData) => {
    const getCargoConfig = (cargo) => {
      const configs = {
        Presidente: { severity: "success" },
        "Vice-Presidente": { severity: "info" },
        Relator: { severity: "warning" },
        Membro: { severity: "secondary" },
      };
      return configs[cargo] || { severity: "secondary" };
    };

    const config = getCargoConfig(rowData.cargo);
    return <Tag value={rowData.cargo} severity={config.severity} />;
  };

  const partidoMembroBody = (rowData) => (
    <span style={{ fontSize: "0.85rem" }}>
      {rowData.partido || "Sem partido"}
    </span>
  );

  const acoesMembroBody = (rowData) => (
    <Button
      icon="pi pi-trash"
      size="small"
      severity="warning"
      tooltip="Remover"
      tooltipOptions={{ position: "left" }}
      className="custom-button button-efeito"
      style={{ width: "32px", height: "32px" }}
      onClick={() => confirmarRemocaoMembro(rowData)}
    />
  );

  const emptyMessage = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 20px",
        color: "#6b7280",
      }}
    >
      <FaUsers size={48} style={{ color: "#9ca3af", marginBottom: "20px" }} />
      <h3 style={{ marginBottom: "8px", color: "#374151" }}>
        Nenhuma comissão encontrada
      </h3>
      <p
        style={{ textAlign: "center", maxWidth: "400px", fontSize: "0.85rem" }}
      >
        Não há comissões para a legislatura selecionada.
      </p>
    </div>
  );

  const emptyMembrosMessage = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        color: "#6b7280",
      }}
    >
      <FaUsers size={32} style={{ color: "#9ca3af", marginBottom: "12px" }} />
      <p>Nenhum membro nesta comissão</p>
    </div>
  );

  const parlamentaresDisponiveis = parlamentares.filter(
    (p) => !membrosComissao.some((m) => m.parlamentar_id === p.id),
  );

  const modalComissaoFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancelar"
        size="small"
        severity="secondary"
        icon="pi pi-times"
        onClick={() => setModalComissaoVisible(false)}
        outlined
      />
      <Button
        label="Salvar"
        size="small"
        severity="success"
        icon="pi pi-check"
        onClick={salvarComissao}
        loading={submitting}
        autoFocus
      />
    </div>
  );

  const totalComissoes = comissoes.length;

  return (
    <div className="dashboard-container">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="dashboard-header">
        <h1>Comissões</h1>
        <p>Gestão das comissões do legislativo</p>
      </div>

      <div className="page-content">
        {/* Tabela de comissões */}
        <Card style={{ padding: "0" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            {/* ESQUERDA */}
            <div style={{ minWidth: "250px" }}>
              <Dropdown
                value={legislaturaSelecionada}
                options={legislaturas}
                onChange={(e) => setLegislaturaSelecionada(e.value)}
                optionLabel="name"
                placeholder="Selecione uma legislatura"
                loading={!legislaturas.length}
              />
            </div>

            {/* DIREITA */}
            <div style={{ display: "flex", gap: "10px" }}>
              <Button
                label="Adicionar"
                icon="pi pi-plus"
                severity="success"
                size="small"
                onClick={abrirModalCriarComissao}
                disabled={loading}
              />
              <Button
                label="Exportar"
                icon="pi pi-file-excel"
                severity="secondary"
                size="small"
                outlined
                onClick={() => console.log("Exportar...")}
              />
            </div>
          </div>

          <div style={{ padding: "0 0 10px 0", width: "100%" }}>
            <Message
              severity="info"
              text={`Legislatura: ${legislaturaSelecionada?.name || "Selecione uma legislatura"} ${legislaturaSelecionada?.atual ? "(Atual)" : ""} • Total de Comissões: ${totalComissoes}`}
              style={{ width: "100%" }}
            />
          </div>

          {loading ? (
            <div className="loading-container">
              <ProgressSpinner />
            </div>
          ) : (
            <DataTable
              value={comissoes}
              dataKey="id"
              style={{ width: "100%" }}
              paginator
              rows={10}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate="{first} a {last} de {totalRecords}"
              emptyMessage={emptyMessage}
              showGridlines
              stripedRows
            >
              <Column
                field="nome"
                header="Comissão"
                body={nomeBody}
                sortable
                style={{ minWidth: "250px" }}
              />
              <Column
                field="sigla"
                header="Sigla"
                sortable
                style={{ width: "100px" }}
              />
              <Column
                field="tipo"
                header="Tipo"
                body={tipoBody}
                sortable
                style={{ width: "180px" }}
              />
              <Column
                field="data_criacao"
                header="Dt Criação"
                body={dataCriacaoBody}
                sortable
                style={{ width: "140px" }}
              />
              <Column
                header="Status"
                body={ativaBody}
                sortable
                style={{ width: "120px" }}
              />
              <Column
                header="Ações"
                body={acoesBody}
                style={{ width: "180px" }}
              />
            </DataTable>
          )}
        </Card>
      </div>

      {/* Modal de Criar/Editar Comissão */}
      <Dialog
        visible={modalComissaoVisible}
        style={{ width: "650px", maxWidth: "90vw" }}
        modal
        className="p-fluid modern-dialog"
        onHide={() => setModalComissaoVisible(false)}
        footer={modalComissaoFooter}
        header={
          <div className="flex align-items-center gap-2">
            <div
              className="bg-primary bg-opacity-10 border-circle p-2"
              style={{ borderRadius: "12px" }}
            >
              <i
                className={`pi ${editandoComissao ? "pi-building" : "pi-plus-circle"} text-primary text-xl`}
              ></i>
            </div>
            <span className="font-bold text-xl">
              {editandoComissao ? "Editar Comissão" : "Nova Comissão"}
            </span>
          </div>
        }
      >
        <div className="modal-form" style={{ padding: 0 }}>
          <div
            className="grid p-fluid"
            style={{
              backgroundColor: "#fafafa",
              border: "1px solid #e8e8e8",
              padding: "15px",
              borderRadius: "12px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
              margin: 0,
            }}
          >
            <div className="col-12">
              <div className="field" style={{ marginBottom: "15px" }}>
                <label className="font-semibold mb-1">Nome da Comissão *</label>
                <InputText
                  value={formDataComissao.nome}
                  onChange={(e) =>
                    setFormDataComissao({
                      ...formDataComissao,
                      nome: e.target.value,
                    })
                  }
                  placeholder="Ex: Comissão de Justiça e Redação"
                />
              </div>
            </div>

            <div className="col-12 md:col-6">
              <div className="field" style={{ marginBottom: "15px" }}>
                <label className="font-semibold mb-1">Sigla</label>
                <InputText
                  value={formDataComissao.sigla}
                  onChange={(e) =>
                    setFormDataComissao({
                      ...formDataComissao,
                      sigla: e.target.value.toUpperCase(),
                    })
                  }
                  placeholder="Ex: CJR"
                  style={{ textTransform: "uppercase" }}
                />
              </div>
            </div>

            <div className="col-12 md:col-6">
              <div className="field" style={{ marginBottom: "15px" }}>
                <label className="font-semibold mb-1">Tipo</label>
                <Dropdown
                  value={formDataComissao.tipo}
                  options={tiposComissao}
                  onChange={(e) =>
                    setFormDataComissao({ ...formDataComissao, tipo: e.value })
                  }
                  placeholder="Selecione o tipo"
                />
              </div>
            </div>

            <div className="col-12 md:col-6">
              <div className="field" style={{ marginBottom: "15px" }}>
                <label className="font-semibold mb-1">Data de Criação</label>
                <Calendar
                  value={formDataComissao.data_criacao}
                  onChange={(e) =>
                    setFormDataComissao({
                      ...formDataComissao,
                      data_criacao: e.value,
                    })
                  }
                  dateFormat="dd/mm/yy"
                  placeholder="dd/mm/aaaa"
                  showIcon
                  showButtonBar
                />
              </div>
            </div>

            <div className="col-12 md:col-6">
              <div className="field" style={{ marginBottom: "15px" }}>
                <label className="font-semibold mb-1">Data de Extinção</label>
                <Calendar
                  value={formDataComissao.data_extincao}
                  onChange={(e) =>
                    setFormDataComissao({
                      ...formDataComissao,
                      data_extincao: e.value,
                    })
                  }
                  dateFormat="dd/mm/yy"
                  placeholder="dd/mm/aaaa"
                  showIcon
                  showButtonBar
                  minDate={formDataComissao.data_criacao}
                />
              </div>
            </div>

            <div className="col-12">
              <div className="field" style={{ marginBottom: "15px" }}>
                <label className="font-semibold mb-1">Observações</label>
                <InputTextarea
                  rows={3}
                  value={formDataComissao.observacoes}
                  onChange={(e) =>
                    setFormDataComissao({
                      ...formDataComissao,
                      observacoes: e.target.value,
                    })
                  }
                  placeholder="Informações adicionais sobre a comissão..."
                />
              </div>
            </div>

            <div className="col-12">
              <div className="field" style={{ marginBottom: 0 }}>
                <div className="flex gap-4 flex-wrap">
                  <div className="flex align-items-center gap-2">
                    <Checkbox
                      inputId="ativa"
                      checked={formDataComissao.ativa}
                      onChange={(e) =>
                        setFormDataComissao({
                          ...formDataComissao,
                          ativa: e.checked,
                        })
                      }
                    />
                    <label htmlFor="ativa" className="font-semibold">
                      Comissão Ativa
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Modal de Membros */}
      <Dialog
        visible={modalMembroVisible}
        style={{ width: "900px", maxWidth: "90vw" }}
        modal
        className="p-fluid modern-dialog"
        onHide={() => setModalMembroVisible(false)}
        header={
          <div className="flex align-items-center gap-2">
            <div
              className="bg-primary bg-opacity-10 border-circle p-2"
              style={{ borderRadius: "12px" }}
            >
              <i className="pi pi-users text-primary text-xl"></i>
            </div>
            <span className="font-bold text-xl">
              Membros da Comissão: {comissaoSelecionada?.nome}
            </span>
          </div>
        }
      >
        <div className="modal-form" style={{ padding: 0 }}>
          {/* Formulário para adicionar membro */}
          <div
            className="grid p-fluid"
            style={{
              backgroundColor: "#fafafa",
              border: "1px solid #e8e8e8",
              padding: "15px",
              borderRadius: "12px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
              margin: "0 0 15px 0",
            }}
          >
            <div className="col-12 md:col-6">
              <div className="field" style={{ marginBottom: "5px" }}>
                <label className="font-semibold mb-1">Parlamentar</label>
                <Dropdown
                  value={formDataMembro.parlamentar_id}
                  options={parlamentaresDisponiveis.map((p) => ({
                    label: `${p.nome}${p.nome_parlamentar ? ` (${p.nome_parlamentar})` : ""} - ${p.partido || "Sem partido"}`,
                    value: p.id,
                  }))}
                  onChange={(e) =>
                    setFormDataMembro({
                      ...formDataMembro,
                      parlamentar_id: e.value,
                    })
                  }
                  placeholder="Selecione um parlamentar"
                  filter
                  showClear
                />
              </div>
            </div>

            <div className="col-12 md:col-4">
              <div className="field" style={{ marginBottom: "5px" }}>
                <label className="font-semibold mb-1">Cargo</label>
                <Dropdown
                  value={formDataMembro.cargo}
                  options={cargosDisponiveis}
                  onChange={(e) =>
                    setFormDataMembro({ ...formDataMembro, cargo: e.value })
                  }
                  placeholder="Selecione o cargo"
                />
              </div>
            </div>

            <div className="col-12 md:col-2">
              <div
                className="field"
                style={{ marginBottom: "5px", marginTop: "24px" }}
              >
                <Button
                  label="Adicionar"
                  icon="pi pi-plus"
                  severity="success"
                  size="small"
                  onClick={adicionarMembro}
                  disabled={!formDataMembro.parlamentar_id || submittingMembro}
                  loading={submittingMembro}
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          </div>

          {/* Tabela de membros */}
          <DataTable
            value={membrosComissao}
            dataKey="id"
            style={{ width: "100%" }}
            emptyMessage={emptyMembrosMessage}
            showGridlines
            stripedRows
          >
            <Column
              header="Parlamentar"
              body={nomeMembroBody}
              sortable
              style={{ minWidth: "250px" }}
            />
            <Column
              field="partido"
              header="Partido"
              body={partidoMembroBody}
              sortable
              style={{ width: "150px" }}
            />
            <Column
              field="cargo"
              header="Cargo"
              body={cargoMembroBody}
              sortable
              style={{ width: "150px" }}
            />
            <Column
              header="Ações"
              body={acoesMembroBody}
              style={{ width: "50px" }}
            />
          </DataTable>
        </div>
      </Dialog>
    </div>
  );
};

export default Comissoes;
